/**
 * StabilityIndicator Component
 * Visual indicator showing device/image stability for document scanning
 * Supports multiple display modes: ring, bar, bubble
 */

import React from 'react';
import { Point } from '@/hooks/useStabilization';

interface StabilityIndicatorProps {
  stabilityScore: number;      // 0-100
  isStable: boolean;
  statusMessage: string;
  mode?: 'ring' | 'bar' | 'bubble';
  size?: 'sm' | 'md' | 'lg';
  showPercentage?: boolean;
  className?: string;
}

interface DocumentOverlayProps {
  corners: Point[] | null;
  isStable: boolean;
  containerWidth: number;
  containerHeight: number;
  videoWidth: number;
  videoHeight: number;
}

// Color based on stability score
const getStabilityColor = (score: number, isStable: boolean): string => {
  if (isStable) return '#22c55e'; // green-500
  if (score >= 70) return '#eab308'; // yellow-500
  if (score >= 40) return '#f97316'; // orange-500
  return '#ef4444'; // red-500
};

const getStabilityColorClass = (score: number, isStable: boolean): string => {
  if (isStable) return 'text-green-500 dark:text-green-400';
  if (score >= 70) return 'text-yellow-500 dark:text-yellow-400';
  if (score >= 40) return 'text-orange-500 dark:text-orange-400';
  return 'text-red-500 dark:text-red-400';
};

const getBgColorClass = (score: number, isStable: boolean): string => {
  if (isStable) return 'bg-green-500';
  if (score >= 70) return 'bg-yellow-500';
  if (score >= 40) return 'bg-orange-500';
  return 'bg-red-500';
};

// Ring Indicator (circular progress)
const RingIndicator: React.FC<StabilityIndicatorProps> = ({
  stabilityScore,
  isStable,
  statusMessage,
  size = 'md',
  showPercentage = true,
}) => {
  const sizes = {
    sm: { ring: 48, stroke: 4, text: 'text-xs' },
    md: { ring: 72, stroke: 6, text: 'text-sm' },
    lg: { ring: 96, stroke: 8, text: 'text-base' },
  };

  const { ring, stroke, text } = sizes[size];
  const radius = (ring - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (stabilityScore / 100) * circumference;
  const color = getStabilityColor(stabilityScore, isStable);

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: ring, height: ring }}>
        <svg
          className="transform -rotate-90"
          width={ring}
          height={ring}
        >
          {/* Background circle */}
          <circle
            cx={ring / 2}
            cy={ring / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            className="text-gray-200 dark:text-gray-700"
          />
          {/* Progress circle */}
          <circle
            cx={ring / 2}
            cy={ring / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-150"
          />
        </svg>
        {/* Center content */}
        {showPercentage && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`font-bold ${text} ${getStabilityColorClass(stabilityScore, isStable)}`}>
              {Math.round(stabilityScore)}%
            </span>
          </div>
        )}
        {/* Stable checkmark */}
        {isStable && !showPercentage && (
          <div className="absolute inset-0 flex items-center justify-center">
            <svg
              className="w-6 h-6 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        )}
      </div>
      {statusMessage && (
        <span className={`${text} font-medium ${getStabilityColorClass(stabilityScore, isStable)}`}>
          {statusMessage}
        </span>
      )}
    </div>
  );
};

// Bar Indicator (horizontal progress)
const BarIndicator: React.FC<StabilityIndicatorProps> = ({
  stabilityScore,
  isStable,
  statusMessage,
  size = 'md',
  showPercentage = true,
}) => {
  const heights = { sm: 'h-2', md: 'h-3', lg: 'h-4' };
  const textSizes = { sm: 'text-xs', md: 'text-sm', lg: 'text-base' };

  return (
    <div className="w-full space-y-1">
      <div className="flex justify-between items-center">
        <span className={`${textSizes[size]} font-medium ${getStabilityColorClass(stabilityScore, isStable)}`}>
          {statusMessage || (isStable ? 'Stable' : 'Stabilizing...')}
        </span>
        {showPercentage && (
          <span className={`${textSizes[size]} font-bold ${getStabilityColorClass(stabilityScore, isStable)}`}>
            {Math.round(stabilityScore)}%
          </span>
        )}
      </div>
      <div className={`w-full ${heights[size]} bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden`}>
        <div
          className={`${heights[size]} ${getBgColorClass(stabilityScore, isStable)} rounded-full transition-all duration-150`}
          style={{ width: `${stabilityScore}%` }}
        />
      </div>
    </div>
  );
};

// Bubble Indicator (floating level)
const BubbleIndicator: React.FC<StabilityIndicatorProps> = ({
  stabilityScore,
  isStable,
  statusMessage,
  size = 'md',
}) => {
  const sizes = { sm: 80, md: 120, lg: 160 };
  const bubbleSizes = { sm: 12, md: 16, lg: 20 };
  const containerSize = sizes[size];
  const bubbleSize = bubbleSizes[size];

  // Calculate bubble position based on stability (centered when stable)
  // Add some random jitter when not stable
  const jitter = isStable ? 0 : (100 - stabilityScore) * 0.3;
  const centerX = containerSize / 2;
  const centerY = containerSize / 2;
  const bubbleX = centerX + (Math.sin(Date.now() / 100) * jitter);
  const bubbleY = centerY + (Math.cos(Date.now() / 100) * jitter);

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className="relative rounded-full border-4 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800"
        style={{ width: containerSize, height: containerSize }}
      >
        {/* Target rings */}
        <div className="absolute inset-4 rounded-full border-2 border-gray-200 dark:border-gray-700" />
        <div className="absolute inset-8 rounded-full border-2 border-gray-200 dark:border-gray-700" />

        {/* Center target */}
        <div
          className="absolute rounded-full bg-green-200 dark:bg-green-900/50"
          style={{
            width: bubbleSize * 2,
            height: bubbleSize * 2,
            left: centerX - bubbleSize,
            top: centerY - bubbleSize,
          }}
        />

        {/* Bubble */}
        <div
          className={`absolute rounded-full transition-all ${
            isStable ? 'bg-green-500 shadow-lg shadow-green-500/50' : 'bg-blue-500'
          }`}
          style={{
            width: bubbleSize,
            height: bubbleSize,
            left: bubbleX - bubbleSize / 2,
            top: bubbleY - bubbleSize / 2,
          }}
        />
      </div>
      {statusMessage && (
        <span className={`text-sm font-medium ${getStabilityColorClass(stabilityScore, isStable)}`}>
          {statusMessage}
        </span>
      )}
    </div>
  );
};

// Main StabilityIndicator component
export const StabilityIndicator: React.FC<StabilityIndicatorProps> = (props) => {
  const { mode = 'ring', className = '' } = props;

  return (
    <div className={className}>
      {mode === 'ring' && <RingIndicator {...props} />}
      {mode === 'bar' && <BarIndicator {...props} />}
      {mode === 'bubble' && <BubbleIndicator {...props} />}
    </div>
  );
};

// Document Edge Overlay (for edge detection mode)
export const DocumentOverlay: React.FC<DocumentOverlayProps> = ({
  corners,
  isStable,
  containerWidth,
  containerHeight,
  videoWidth,
  videoHeight,
}) => {
  if (!corners || corners.length !== 4) {
    return null;
  }

  // Scale corners from video coordinates to container coordinates
  const scaleX = containerWidth / videoWidth;
  const scaleY = containerHeight / videoHeight;

  const scaledCorners = corners.map(c => ({
    x: c.x * scaleX,
    y: c.y * scaleY,
  }));

  const pointsString = scaledCorners.map(c => `${c.x},${c.y}`).join(' ');
  const color = isStable ? '#22c55e' : '#3b82f6';

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox={`0 0 ${containerWidth} ${containerHeight}`}
    >
      {/* Document outline */}
      <polygon
        points={pointsString}
        fill={isStable ? 'rgba(34, 197, 94, 0.1)' : 'rgba(59, 130, 246, 0.1)'}
        stroke={color}
        strokeWidth="3"
        strokeLinejoin="round"
      />
      {/* Corner markers */}
      {scaledCorners.map((corner, index) => (
        <g key={index}>
          <circle
            cx={corner.x}
            cy={corner.y}
            r="8"
            fill={color}
            stroke="white"
            strokeWidth="2"
          />
        </g>
      ))}
    </svg>
  );
};

// Algorithm Selector Dropdown
interface AlgorithmSelectorProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string; description: string }[];
  disabled?: boolean;
  className?: string;
}

export const AlgorithmSelector: React.FC<AlgorithmSelectorProps> = ({
  value,
  onChange,
  options,
  disabled = false,
  className = '',
}) => {
  return (
    <div className={`relative ${className}`}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full px-4 py-3 text-sm font-medium bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed appearance-none cursor-pointer"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {/* Dropdown arrow */}
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <svg
          className="w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>
    </div>
  );
};

// Compact stability badge for inline display
interface StabilityBadgeProps {
  stabilityScore: number;
  isStable: boolean;
  className?: string;
}

export const StabilityBadge: React.FC<StabilityBadgeProps> = ({
  stabilityScore,
  isStable,
  className = '',
}) => {
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
        isStable
          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
          : stabilityScore >= 70
          ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
          : stabilityScore >= 40
          ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
          : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
      } ${className}`}
    >
      <div
        className={`w-2 h-2 rounded-full ${
          isStable ? 'bg-green-500 animate-pulse' : getBgColorClass(stabilityScore, isStable)
        }`}
      />
      <span>{isStable ? 'Stable' : `${Math.round(stabilityScore)}%`}</span>
    </div>
  );
};

export default StabilityIndicator;
